import { AlertTriangle, Check, InfoCircle, Trash02, XCircle, XClose } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { type DismissReason, DismissReasonPopover } from "./DismissReasonPopover";

export type RiskEvidence = { timestamp: string; text: string; translation?: string };

type RiskCardBaseProps = {
  icon: ReactNode;
  title: string;
  severity: "critical" | "high" | "medium" | "low";
  tooltipInfo?: string;
  evidence: RiskEvidence[];
  reviewReason?: string | null;
  typeOfRisk?: "default" | "custom";
  flaggedByName?: string;
  flaggedAt?: string;
};

type RiskCardProps = RiskCardBaseProps &
  (
    | { readOnly: true }
    // A risk a reviewer added by hand is deleted outright rather than dismissed with a reason.
    | { readOnly?: false; onDelete: () => void }
    | {
        readOnly?: false;
        riskId?: string | null;
        /** Set once the reviewer dismissed the flag, with the reason they gave. */
        dismissal?: DismissReason;
        onDismiss: (dismissal: DismissReason) => void;
        onConfirm: () => void;
      }
  );

const DismissalFooter = ({ dismissal }: { dismissal: DismissReason }) => (
  <div className="-mx-3 -mb-3 mt-2 flex flex-col gap-1 rounded-b-md border-t border-border-secondary bg-white px-3 py-3">
    <div className="flex items-center gap-1.5 text-sm">
      <XCircle aria-hidden="true" className="size-4 shrink-0 text-fg-error-primary" />
      <span className="font-medium text-error-primary">Dismissed:</span>
      <span className="text-tertiary">{dismissal.reason}</span>
    </div>
    {dismissal.note && (
      <p className="ml-2 border-l-2 border-border-secondary pl-3 text-sm italic text-tertiary">{dismissal.note}</p>
    )}
  </div>
);

export const RiskCard = (props: RiskCardProps) => {
  const { icon, title, tooltipInfo, evidence, reviewReason, typeOfRisk = "default", flaggedByName, flaggedAt } = props;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const reviewable = !props.readOnly && "onDismiss" in props ? props : undefined;
  const dismissal = reviewable?.dismissal;

  return (
    <div
      className={cx(
        "border border-border-primary rounded-md p-3 flex flex-col min-h-[10px]",
        dismissal ? "bg-secondary" : "bg-white",
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {icon}
          <span className="text-sm font-medium text-primary truncate min-w-0">{title}</span>
          {tooltipInfo && (
            <Tooltip title={tooltipInfo} placement="top">
              <TooltipTrigger className="text-quaternary hover:text-fg-quaternary_hover shrink-0">
                <InfoCircle className="size-4" />
              </TooltipTrigger>
            </Tooltip>
          )}
        </div>
        {!props.readOnly && "onDelete" in props && (
          <Button
            color="secondary"
            size="sm"
            aria-label="Delete risk"
            iconLeading={<Trash02 className="w-4 h-4" />}
            onClick={props.onDelete}
          />
        )}
        {reviewable && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              color={dismissal ? "secondary" : "primary"}
              size="sm"
              aria-label="Confirm risk"
              iconLeading={<Check className="w-4 h-4" />}
              onClick={reviewable.onConfirm}
            />
            <DismissReasonPopover
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
              riskId={reviewable.riskId}
              riskName={title}
              saved={dismissal}
              onSubmit={reviewable.onDismiss}
            >
              <Button
                color={dismissal ? "primary-destructive" : "secondary"}
                size="sm"
                aria-label="Dismiss risk"
                iconLeading={<XClose className="w-4 h-4" />}
                onClick={() => setPopoverOpen(true)}
              />
            </DismissReasonPopover>
          </div>
        )}
      </div>

      {reviewReason && (
        <div className={cx("my-2 flex gap-2 rounded-md bg-warning-primary p-2.5", dismissal && "opacity-50")}>
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-fg-warning-primary" />
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-secondary">Why this needs review</span>
            <p className="text-sm text-secondary">{reviewReason}</p>
          </div>
        </div>
      )}

      {/* Evidence */}
      <p className={cx("text-sm text-quaternary leading-6", dismissal && "opacity-50")}>
        {evidence.map((ev, i) => (
          <span key={`${ev.timestamp}-${i}`}>
            {ev.timestamp && (
              <button type="button" className="text-quaternary underline decoration-dotted mr-1.5">
                {ev.timestamp}
              </button>
            )}
            {ev.text}
            {ev.translation && <em> ({ev.translation})</em>}
            <br />
          </span>
        ))}
      </p>

      {typeOfRisk === "custom" && (
        <p className="text-sm text-quaternary">
          Flagged by {flaggedByName || "Unknown"} • {flaggedAt}
        </p>
      )}

      {dismissal && <DismissalFooter dismissal={dismissal} />}
    </div>
  );
};
